import { RecipientCompany } from '@hooks-api';

/**
 * The two exclusion sets the sync endpoint accepts. Mirrors the backend's
 * `assembleRecipients` model exactly (see easybiz-backend §6.2):
 * - a company in `companyIds` is skipped wholesale — but a member who also
 *   belongs to another, included company still receives (via that company);
 * - a user in `userIds` is dropped **everywhere** (global), because dedupe
 *   collapses them to a single recipient regardless of company.
 * The tree therefore starts empty (nothing excluded = everyone in).
 */
export interface Exclusions {
  companyIds: string[];
  userIds: string[];
}

export const EMPTY_EXCLUSIONS: Exclusions = { companyIds: [], userIds: [] };

/** AntD tree key for a member instance (unique even when a user spans companies). */
export const memberKey = (companyId: string, userId: string) => `${companyId}:${userId}`;

/** True if this member will not receive under the current exclusions. */
const isMemberExcluded = (companyId: string, userId: string, ex: Exclusions) =>
  ex.companyIds.includes(companyId) || ex.userIds.includes(userId);

/** Members of a company that will still receive under the current exclusions. */
const selectedMembers = (company: RecipientCompany, ex: Exclusions) =>
  company.members.filter((m) => !isMemberExcluded(company.companyId, m.userId, ex));

/**
 * AntD `checkedKeys` derived from the exclusion sets: every non-excluded member
 * instance, plus a company key when all its members are still in.
 */
export const computeCheckedKeys = (companies: RecipientCompany[], ex: Exclusions): string[] => {
  const keys: string[] = [];
  for (const company of companies) {
    const selected = selectedMembers(company, ex);
    for (const m of selected) keys.push(memberKey(company.companyId, m.userId));
    if (selected.length === company.members.length) keys.push(company.companyId);
  }
  return keys;
};

const without = (list: string[], value: string) => list.filter((v) => v !== value);
const withValue = (list: string[], value: string) => (list.includes(value) ? list : [...list, value]);

/** Toggle a whole company on/off, returning the next exclusion sets. */
export const toggleCompany = (
  ex: Exclusions,
  company: RecipientCompany,
  checked: boolean
): Exclusions => {
  const memberIds = company.members.map((m) => m.userId);
  if (checked) {
    // Include the company and re-include all of its members.
    return {
      companyIds: without(ex.companyIds, company.companyId),
      userIds: ex.userIds.filter((id) => !memberIds.includes(id)),
    };
  }
  // Exclude the company wholesale (members shown unchecked here, but a shared
  // member stays checked under any other company they belong to).
  return { ...ex, companyIds: withValue(ex.companyIds, company.companyId) };
};

/** Toggle a single member on/off (global by userId), returning the next sets. */
export const toggleMember = (
  ex: Exclusions,
  company: RecipientCompany,
  userId: string,
  checked: boolean
): Exclusions => {
  if (checked) {
    let companyIds = ex.companyIds;
    let userIds = without(ex.userIds, userId);
    // Re-checking a member of a wholesale-excluded company lifts the company
    // exclusion and instead excludes its other members individually.
    if (ex.companyIds.includes(company.companyId)) {
      companyIds = without(ex.companyIds, company.companyId);
      for (const m of company.members) {
        if (m.userId !== userId) userIds = withValue(userIds, m.userId);
      }
    }
    return { companyIds, userIds };
  }
  return { ...ex, userIds: withValue(ex.userIds, userId) };
};

export interface CoverageException {
  companyId: string;
  companyName: string;
  selected: number;
  total: number;
}

export interface Coverage {
  /** Companies where no member will receive. */
  excluded: CoverageException[];
  /** Companies where some — but not all — members will receive. */
  partial: CoverageException[];
}

/** Split companies into fully-excluded vs partially-covered for the exceptions list. */
export const computeCoverage = (companies: RecipientCompany[], ex: Exclusions): Coverage => {
  const excluded: CoverageException[] = [];
  const partial: CoverageException[] = [];
  for (const company of companies) {
    const total = company.members.length;
    const selected = selectedMembers(company, ex).length;
    const entry: CoverageException = {
      companyId: company.companyId,
      companyName: company.companyName,
      selected,
      total,
    };
    if (selected === 0) excluded.push(entry);
    else if (selected < total) partial.push(entry);
  }
  return { excluded, partial };
};

/** Unique recipients (dedupe by lowercased email) that will actually be synced. */
export const uniqueRecipientCount = (companies: RecipientCompany[], ex: Exclusions): number => {
  const emails = new Set<string>();
  for (const company of companies) {
    for (const m of selectedMembers(company, ex)) emails.add(m.email.toLowerCase());
  }
  return emails.size;
};
